import React, { useState } from "react"
import PropTypes from "prop-types"
import { Modal, Form, Input, Button, DatePicker, Flex } from "antd"
import styles from "./index.module.css"
import bgMan from "assets/imagesV2/logo_man.svg"
import bgWoman from "assets/imagesV2/logo_woman.svg"
import { useTheme } from "context/AntdThemeContext"
import { themes } from "constants/theme"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"
import { useTranslation } from "react-i18next"
import { validatePhoneNumber, PhoneInput } from "ui/Input/PhoneInput"
import { postWaiverSubmission, getWaiverSubmissionsByTripBooking } from "store/digital-waiver"
import { useDispatch, useSelector } from "react-redux"
import { toaster } from "components/common/Toast"
import dayjs from "dayjs"
export default function ParentModal({ uuid, userId, tripBookingId, id, waiver, visible, onCancel, onSignDocumentOpen, dobDefaultPickerValue = false }) {
  const defaultPickerValue = dobDefaultPickerValue ? dayjs().subtract(18, "year") : undefined
  const { t } = useTranslation()
  const { theme } = useTheme()
  const [parentData, setParentData] = useState({})
  const logoSrc = theme === themes.sportswoman ? bgWoman : bgMan
  const { loading: postLoading } = useSelector((store) => store?.digitalWaiver) || {}
  const guestData = useSelector((state) => state.digitalWaiver.waiverSubmissionsByTripBooking.guest_data) || []
  // The primary guest (main member) is flagged in guest_data. Their submission is
  // "primary" only while they are still unsigned; any later direct adult signing is a guest.
  const primaryGuest = guestData.find((g) => g.is_primary_guest)
  const isSigningPrimary = Boolean(primaryGuest) && primaryGuest.status !== "Signed"
  const [form] = Form.useForm()
  const dispatch = useDispatch()

  const handleNext = async () => {
    try {
      const values = await form.validateFields()
      // setParentData(values)
      const formData = new FormData()
      // Add participant data to formData
      formData.append("waiver_submission[submission_for]", "adult")
      formData.append("waiver_submission[waiver_participants_attributes][0][participant_type]", "adult")
      formData.append("waiver_submission[waiver_participants_attributes][0][first_name]", values.firstName || "")
      formData.append("waiver_submission[waiver_participants_attributes][0][last_name]", values.lastName || "")
      formData.append("waiver_submission[waiver_participants_attributes][0][email]", values.email || "")
      formData.append("waiver_submission[waiver_participants_attributes][0][phone_number]", values.phone?.fullNumber || "")
      // formData.append('waiver_submission[waiver_participants_attributes][0][birthdate]', values.dob ? new Date(values.dob).toLocaleDateString('en-GB').replaceAll('/', '-') : '');
      // Format date as DD-MM-YYYY
      const formattedDate = values.dob ? new Date(values.dob).toLocaleDateString("en-GB").replaceAll("/", "-") : ""
      formData.append("waiver_submission[waiver_participants_attributes][0][birthdate]", formattedDate)
      if (id) {
        formData.append("waiver_submission[shared_waiver_id]", id)
        formData.append("waiver_submission[user_type]", "guest")
      } else if (userId) {
        formData.append("waiver_submission[user_id]", userId)
        formData.append("waiver_submission[user_type]", isSigningPrimary ? "primary" : "guest")
      }
      // Only set trip_booking_id once, prefer tripBookingId, then waiver.trip_booking_id
      if (tripBookingId) {
        formData.append("waiver_submission[trip_booking_id]", tripBookingId)
      } else if (waiver?.trip_booking_id) {
        formData.append("waiver_submission[trip_booking_id]", waiver.trip_booking_id)
      }
      dispatch(postWaiverSubmission(formData)).then((res) => {
        toaster.success(res?.message)
        if (uuid) {
          dispatch(getWaiverSubmissionsByTripBooking(uuid))
        }
        // Call parent to open SignDocumentModal and pass data
        if (onSignDocumentOpen) {
          onSignDocumentOpen(res?.data?.id, res?.data?.pending_waiver_submissions || [])
        }
        // Close parent modal
        handleCancel()
      })
      // If validation succeeds, open the signing modal
    } catch (err) {
      // Validation failed, errors will show automatically
    }
  }

  const handleCancel = () => {
    form.resetFields()
    if (onCancel) onCancel()
  }

  return (
    <Modal
        maskClosable={false}
        open={visible}
        onCancel={handleCancel}
        footer={null}
        closable={true}
        centered
        width={900}
        className={`${styles.modalContainer}`}
      >
        <div className={`pb-xl ${styles.title}`}>{t("embedded.booking_confirmation.parent_or_guardian")}</div>
        <Form layout="vertical" form={form}>
          <div className={styles.formRow}>
            <div className={styles.formCol}>
              <LabeledField
                labelKey={t("embedded.booking_confirmation.first_name")}
                name="firstName"
                rules={[{ required: true, message: "Enter your first name" }]}
              >
                <Input placeholder="Enter your first name" />
              </LabeledField>
              <LabeledField
                labelKey={t("embedded.booking_confirmation.email")}
                name="email"
                rules={[{ required: true, message: "Enter your email" }]}
              >
                <Input placeholder="example@email.com" />
              </LabeledField>
              <LabeledField
                labelKey={t("embedded.booking_confirmation.dob")}
                name="dob"
                rules={[
                  { required: true, message: "Enter your date of birth" },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve()

                      const today = new Date()
                      const birthDate = new Date(value)
                      const age = today.getFullYear() - birthDate.getFullYear()
                      const monthDiff = today.getMonth() - birthDate.getMonth()
                      const dayDiff = today.getDate() - birthDate.getDate()

                      const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age

                      if (actualAge < 18) {
                        return Promise.reject("Date of birth should be greater than 18 years")
                      }

                      return Promise.resolve()
                    }
                  }
                ]}
              >
                <DatePicker className="w-full p-xs" style={{ width: "100%" }} placeholder="MM/DD/YYYY" format="MM/DD/YYYY" defaultPickerValue={defaultPickerValue} />
              </LabeledField>
            </div>
            <div className={styles.formCol}>
              <LabeledField
                labelKey={t("embedded.booking_confirmation.last_name")}
                name="lastName"
                rules={[{ required: true, message: "Enter your last name" }]}
              >
                <Input placeholder="Enter your last name" />
              </LabeledField>
              <LabeledField
                labelKey={t("embedded.booking_confirmation.phone_number")}
                name="phone"
                rules={[
                  {required: true,
                    validator: (_, value) => {
                      if (!value?.fullNumber?.trim()) {
                        return Promise.reject(t("form_validation.phone_required"))
                      }
                      return validatePhoneNumber(value.fullNumber)
                    }
                  }
                ]}
              >
                <PhoneInput
                  initialCountryCode={parentData?.phone?.countryCode}
                  initialValue={parentData?.phone?.fullNumber}
                  onChange={(value) => setParentData({ ...parentData, phone: value })}
                />
              </LabeledField>
            </div>
          </div>
          <Flex justify="space-between" align="center" gap={8} className={`pt-4xl`}>
            <Flex align="center" gap={12}>
              {t("settings.powered_by")}
              <img src={logoSrc} alt="Digital Sportsman" className={styles.poweredLogo} />
            </Flex>
            <Flex align="center" gap={8}>
              <Button onClick={handleCancel} color="primary" variant="outlined">
                {t("embedded.booking_confirmation.previous")}
              </Button>
              <Button loading={postLoading} disabled={postLoading} htmlType="button" color="primary" variant="solid" onClick={handleNext}>
                {t("embedded.booking_confirmation.next")}
              </Button>
            </Flex>
          </Flex>
        </Form>
      </Modal>
  )
}

ParentModal.propTypes = {
  uuid: PropTypes.string,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tripBookingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  id: PropTypes.string,
  waiver: PropTypes.object,
  visible: PropTypes.bool,
  onCancel: PropTypes.func,
  onSignDocumentOpen: PropTypes.func,
  dobDefaultPickerValue: PropTypes.bool
}
